import Button from 'devextreme-react/button';
import Switch from 'devextreme-react/switch';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { showErrorToast, showSuccessToast } from '../notifications/toast';
import type {
  SiteFeedbackApi,
  SiteSupportMetaCategory,
  SiteSupportMetaOption
} from './types';

const SECTIONS: { category: SiteSupportMetaCategory; title: string; hint: string }[] = [
  {
    category: 'BugSeverity',
    title: 'Bug severity',
    hint: 'Choices shown on the Report Bug form for reporters.'
  },
  {
    category: 'BugStatus',
    title: 'Bug status',
    hint: 'Statuses admins can apply on bug tickets (Open, In-Progress, …).'
  },
  {
    category: 'FeedbackType',
    title: 'Feedback type',
    hint: 'Choices shown on the Share Feedback form (General, Feature, Change, …).'
  },
  {
    category: 'FeedbackStatus',
    title: 'Feedback status',
    hint: 'Statuses for feedback tickets. General often closes quickly; Feature/Change use Planned / In Progress / Done.'
  },
  {
    category: 'Priority',
    title: 'Priority',
    hint: 'Priority choices for Feature/Change feedback tickets.'
  },
  {
    category: 'Workload',
    title: 'Workload',
    hint: 'Sizing options (XS–XL) for bug and feedback tickets.'
  }
];

export interface SupportMetadataAdminPageProps {
  api: SiteFeedbackApi;
}

export function SupportMetadataAdminPage({ api }: SupportMetadataAdminPageProps) {
  const [rows, setRows] = useState<SiteSupportMetaOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNames, setNewNames] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.listAdminMeta());
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not load support metadata.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const byCategory = useMemo(() => {
    const map = new Map<string, SiteSupportMetaOption[]>();
    for (const section of SECTIONS) map.set(section.category, []);
    for (const row of rows) {
      const list = map.get(row.category) ?? [];
      list.push(row);
      map.set(row.category, list);
    }
    for (const [key, list] of map) {
      map.set(
        key,
        [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      );
    }
    return map;
  }, [rows]);

  async function onCreate(category: SiteSupportMetaCategory) {
    const name = (newNames[category] ?? '').trim();
    if (!name) return;
    try {
      await api.createMeta({ category, name });
      setNewNames((prev) => ({ ...prev, [category]: '' }));
      showSuccessToast('Option created.');
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not create option.');
    }
  }

  async function saveRename(row: SiteSupportMetaOption) {
    const name = editingName.trim();
    if (!name || name === row.name) {
      setEditingId(null);
      return;
    }
    try {
      await api.updateMeta(row.id, { name });
      showSuccessToast('Option renamed.');
      setEditingId(null);
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Rename failed.');
    }
  }

  async function moveOption(category: SiteSupportMetaCategory, index: number, direction: -1 | 1) {
    const list = byCategory.get(category) ?? [];
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= list.length) return;
    const reordered = [...list];
    [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
    try {
      await Promise.all(
        reordered.map((row, position) => api.updateMeta(row.id, { sortOrder: (position + 1) * 10 }))
      );
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not reorder options.');
    }
  }

  async function toggleActive(row: SiteSupportMetaOption, isActive: boolean) {
    try {
      await api.updateMeta(row.id, { isActive });
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not update option.');
    }
  }

  return (
    <div className="ops-page site-support-admin site-support-meta-admin">
      <h2 className="dashboard-title">Support Metadata</h2>
      <p className="site-support-hint site-support-meta-admin__intro">
        Customize choice lists used on bug reports and feedback. Inactive options stay on existing
        records but are hidden from new submissions.
      </p>

      {loading ? <p className="site-support-hint">Loading…</p> : null}

      {SECTIONS.map((section) => {
        const options = byCategory.get(section.category) ?? [];
        return (
          <section key={section.category} className="site-support-meta-section" aria-labelledby={`meta-${section.category}`}>
            <div className="site-support-meta-section__head">
              <h3 id={`meta-${section.category}`} className="site-support-section-title">
                {section.title}
              </h3>
              <p className="site-support-hint">{section.hint}</p>
            </div>

            <div className="site-support-meta-add">
              <TextBox
                value={newNames[section.category] ?? ''}
                width="100%"
                placeholder={`Add ${section.title.toLowerCase()}…`}
                onValueChanged={(e) =>
                  setNewNames((prev) => ({ ...prev, [section.category]: String(e.value ?? '') }))
                }
              />
              <Button text="Add" type="default" onClick={() => void onCreate(section.category)} />
            </div>

            <ul className="site-support-meta-list">
              {options.length === 0 ? (
                <li className="site-support-hint">No options yet.</li>
              ) : (
                options.map((row, index) => (
                  <li key={row.id} className={!row.isActive ? 'is-inactive' : undefined}>
                    <div className="site-support-meta-list__main">
                      {editingId === row.id ? (
                        <TextBox
                          value={editingName}
                          width="100%"
                          onValueChanged={(e) => setEditingName(String(e.value ?? ''))}
                          onEnterKey={() => void saveRename(row)}
                          onFocusOut={() => void saveRename(row)}
                        />
                      ) : (
                        <button
                          type="button"
                          className="site-support-meta-list__name"
                          onClick={() => {
                            setEditingId(row.id);
                            setEditingName(row.name);
                          }}
                        >
                          {row.name}
                        </button>
                      )}
                      <span className="site-support-hint">#{row.sortOrder}</span>
                    </div>
                    <div className="site-support-meta-list__actions">
                      <Button
                        icon="arrowup"
                        stylingMode="text"
                        hint="Move up"
                        disabled={index === 0}
                        onClick={() => void moveOption(section.category, index, -1)}
                      />
                      <Button
                        icon="arrowdown"
                        stylingMode="text"
                        hint="Move down"
                        disabled={index === options.length - 1}
                        onClick={() => void moveOption(section.category, index, 1)}
                      />
                      <Switch
                        value={row.isActive}
                        hint={row.isActive ? 'Active' : 'Inactive'}
                        onValueChanged={(e) => void toggleActive(row, Boolean(e.value))}
                      />
                    </div>
                  </li>
                ))
              )}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
